/**
 * Stage progression — docs/BUSINESS-RULES.md.
 *
 * A job's stage is computed from checklist progress unless a user has overridden
 * it (`stage_manual`). The order below matters: later rules win, so a job whose
 * contract is signed reads as `signed` even though `Verified` is also done.
 */

import type { ChecklistPhase, JobChecklistItem, JobStage } from "./types.js";

export const STAGE_LABEL: Record<JobStage, string> = {
  new_lead: "New Lead",
  contacted: "Contacted",
  inspection_scheduled: "Inspection Scheduled",
  inspected: "Inspected",
  proposal_sent: "Proposal Sent",
  signed: "Signed",
  in_production: "In Production",
  complete: "Complete",
  closed: "Closed",
  lost: "Lost",
};

/** Stages that count as sold, for the Master list. */
export const SOLD_STAGES: readonly JobStage[] = [
  "signed",
  "in_production",
  "complete",
  "closed",
];

/**
 * The four columns of a sales rep's pipeline.
 *
 * `tone` names the column's colour, which the pipeline paints on the header and
 * nothing else — the card colours mean other things (repair vs replacement, and
 * the follow-up clock), so the column has to own exactly one signal.
 *
 * A job is filed by `pipelineColumn`, not by `stages` alone: Signed — Needs
 * SENDS is decided by whether the handoff went, which no stage says (ADR 0300).
 */
export type PipelineTone = "green" | "slate" | "violet" | "blue";

export type PipelineColumnKey =
  "new_leads" | "estimating" | "needs_sends" | "ongoing";

export const PIPELINE_COLUMNS: readonly {
  key: PipelineColumnKey;
  title: string;
  tone: PipelineTone;
  stages: readonly JobStage[];
}[] = [
  {
    key: "new_leads",
    title: "New Leads",
    tone: "green",
    stages: ["new_lead", "contacted", "inspection_scheduled"],
  },
  {
    key: "estimating",
    title: "Estimating",
    // Slate, not amber. Jonathon, 2026-08-19: "i'm not a big fan of orange."
    tone: "slate",
    stages: ["inspected", "proposal_sent"],
  },
  {
    key: "needs_sends",
    // Jonathon, 2026-10-24: "there needs to be a column in all of our
    // dashboards for signed jobs but missing the SEND" (ADR 0300).
    title: "Signed — Needs SENDS",
    tone: "violet",
    stages: [],
  },
  {
    key: "ongoing",
    title: "Ongoing Jobs",
    tone: "blue",
    stages: ["signed", "in_production", "complete"],
  },
];

/**
 * Signed, and the SENDS handoff to production has not gone (ADR 0300).
 *
 * Signed is a signed contract or a stage at Signed or past it, so a job moved
 * on by hand without anyone pressing Sends still counts — the column means
 * the email never went, whatever the stage says. Pressing Sends is the only
 * way out short of the job being lost or closed.
 */
export function needsSends(job: {
  stage: JobStage;
  sends_at: string | null;
  signed_contract_at?: string | null;
}): boolean {
  if (job.sends_at) return false;
  if (job.stage === "lost" || job.stage === "closed") return false;
  return (
    Boolean(job.signed_contract_at) ||
    job.stage === "signed" ||
    job.stage === "in_production" ||
    job.stage === "complete"
  );
}

/**
 * Which column of a rep's pipeline a job sits in, or null for the two stages
 * the pipeline leaves off (lost and closed).
 */
export function pipelineColumn(job: {
  stage: JobStage;
  sends_at: string | null;
  signed_contract_at?: string | null;
}): PipelineColumnKey | null {
  if (needsSends(job)) return "needs_sends";
  return (
    PIPELINE_COLUMNS.find((column) => column.stages.includes(job.stage))?.key ??
    null
  );
}

type ChecklistLike = Pick<JobChecklistItem, "phase" | "label" | "status">;

const isDone = (
  items: readonly ChecklistLike[],
  phase: ChecklistPhase,
  label: string,
): boolean =>
  items.some(
    (i) => i.phase === phase && i.label === label && i.status === "done",
  );

/**
 * The stage a job should be in, given its checklist, whether its contract is
 * signed and whether it has been handed to production.
 *
 * A signed contract makes a job Signed whichever checklist it carries. The
 * insurance checklist once had no "Signed Contract" step for a signature to
 * tick, so on that tick alone an insurance job sat at Proposal Sent after the
 * customer signed (job 1108, 2026-10-22; ADR 0296). Since 2026-10-31 every
 * checklist carries the step (ADR 0326), and the signed date still counts on
 * its own.
 *
 * Unticking Signed Contract unwinds the sale (ADR 0326): `unwind_sale` clears
 * the signed date, ticks "Estimate Sent" if a paper contract skipped it and
 * writes Proposal Sent, which is what this function then gives for the same
 * checklist. `supabase/test/an-unwound-sale-can-be-signed-again.test.ts`
 * holds the two to that.
 *
 * The database has a copy of the last three steps: `close_job` writes
 * `closed` and `reopen_job` works out complete / in production / signed,
 * because Kelsey's board never opens the job (migrations 20261012130000 and
 * 20261013100000, ADR 0273).
 * `supabase/test/close-and-reopen-move-a-hand-set-stage.test.ts` holds the
 * two to the same answer; change one and change the other.
 */
export function computeStage(
  items: readonly ChecklistLike[],
  job: { sends_at: string | null; signed_contract_at?: string | null },
): JobStage {
  let stage: JobStage = "new_lead";
  if (isDone(items, "prospect", "Verified")) stage = "contacted";
  if (isDone(items, "prospect", "Inspection Scheduled"))
    stage = "inspection_scheduled";
  if (isDone(items, "prospect", "Roof Measurement")) stage = "inspected";
  if (isDone(items, "prospect", "Estimate Sent")) stage = "proposal_sent";
  if (isDone(items, "prospect", "Signed Contract") || job.signed_contract_at)
    stage = "signed";
  if (job.sends_at) stage = "in_production";
  if (isDone(items, "production", "Final Check")) stage = "complete";
  if (
    isDone(items, "followup", "Closed") ||
    isDone(items, "production", "Closed")
  )
    stage = "closed";
  return stage;
}

/** The next pending prospect step, shown as "Next Step" on the overview. */
export function nextStep(
  items: readonly ChecklistLike[],
  job: { sends_at: string | null },
): string {
  const next = items
    .filter((i) => i.phase === "prospect")
    .find((i) => i.status === "pending");
  if (next) return next.label;
  return job.sends_at ? "Work the approved checklist" : "—";
}

/** Cycle a checklist item: tristate items can also be N/A. */
export function nextItemStatus(item: {
  item_type: string;
  status: string;
}): "pending" | "done" | "na" {
  if (item.item_type === "tristate") {
    if (item.status === "pending") return "done";
    if (item.status === "done") return "na";
    return "pending";
  }
  return item.status === "done" ? "pending" : "done";
}
